import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { BarChart3, Download, Users, Clock, TrendingUp, Filter, MessageSquare, FileText, FileSpreadsheet } from 'lucide-react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { useAuth } from '@/hooks/useApi';
import { useTenantContext } from '@/contexts/TenantContext';
import { apiClient } from '@/lib/api';
import { SMSAnalytics } from '@/components/SMSAnalytics';
import { useTenantBranding } from '@/hooks/useTenantBranding';

interface ReportData {
  avgWaitTimes: Array<{ queue: string; customers: number; journeys?: number; unique_customers?: number; avgWait: number; }>;
  queueDetails: Array<{ queue: string; customers: number; journeys?: number; unique_customers?: number; avgWait: number; }>;
  throughput: Array<{ date: string; completed: number; }>;
  waitTimesByDay: Array<{ date: string; avgWait: number; }>;
  waitTimesByHour: Array<{ hour: string; avgWait: number; }>;
  customersByDay: Array<{ date: string; customers: number; }>;
  customersByQueue: Array<{ queue: string; customers: number; }>;
  throughputByHour: Array<{ hour: string; customers: number; }>;
  hourlyActivity: Array<{ hour: string; entries: number; service_starts: number; completions: number; }>;
  overviewTable: Array<{ queue: string; avgWait: number; throughput: number; customers: number; smsIn: number; smsOut: number; smsTotal: number; serviceTime: number; }>;
  totalSmsCount?: number;
}

export function Reports() {
  const { user, isSuperAdmin } = useAuth();
  const { selectedTenantId } = useTenantContext();
  const { getPrimaryButtonClass } = useTenantBranding();
  const [userTimezone, setUserTimezone] = useState('UTC');
  const [reportData, setReportData] = useState<ReportData>({
    avgWaitTimes: [],
    queueDetails: [],
    throughput: [],
    waitTimesByDay: [],
    waitTimesByHour: [],
    customersByDay: [],
    customersByQueue: [],
    throughputByHour: [],
    hourlyActivity: [],
    overviewTable: []
  });
  const [showDailyBreakdown, setShowDailyBreakdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [selectedPreset, setSelectedPreset] = useState('mtd');

  const datePresets = [
    { id: 'today', label: 'Today' },
    { id: 'yesterday', label: 'Yesterday' },
    { id: 'wtd', label: 'Week to Date' },
    { id: 'mtd', label: 'Month to Date' },
    { id: 'lastmonth', label: 'Last Month' },
    { id: 'last7', label: 'Last 7 Days' },
    { id: 'last30', label: 'Last 30 Days' },
    { id: 'custom', label: 'Custom Range' }
  ];

  const applyDatePreset = (presetId: string) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    let start: Date, end: Date;
    
    switch (presetId) {
      case 'today':
        start = end = today;
        break;
      case 'yesterday':
        start = end = new Date(today.getTime() - 24 * 60 * 60 * 1000);
        break;
      case 'wtd':
        const dayOfWeek = today.getDay();
        start = new Date(today.getTime() - dayOfWeek * 24 * 60 * 60 * 1000);
        end = today;
        break;
      case 'mtd':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = today;
        break;
      case 'lastmonth':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0); // Last day of previous month
        break;
      case 'last7':
        start = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000);
        end = today;
        break;
      case 'last30':
        start = new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000);
        end = today;
        break;
      default:
        return;
    }
    
    setDateRange({
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    });
  };

  const activeTenantId = selectedTenantId || user?.tenant_from_group;

  useEffect(() => {
    if (activeTenantId && userTimezone) {
      loadReportData();
    }
  }, [activeTenantId, dateRange, userTimezone]);
  
  // Load user timezone and apply default preset on component mount
  useEffect(() => {
    const loadUserTimezone = async () => {
      try {
        const meResponse = await apiClient.getMe();
        setUserTimezone(meResponse.timezone || 'UTC');
        // Apply default MTD preset after timezone is loaded
        applyDatePreset('mtd');
      } catch (error) {
        console.error('Failed to load user timezone:', error);
        // Still apply preset even if timezone fails
        applyDatePreset('mtd');
      }
    };
    loadUserTimezone();
  }, []);

  // Add key prop to SMSAnalytics to force re-render when dateRange changes
  const smsAnalyticsKey = `${activeTenantId}-${dateRange.start}-${dateRange.end}`;

  const loadReportData = async () => {
    if (!activeTenantId) {
      console.log('No activeTenantId, skipping report load');
      return;
    }

    console.log('Loading reports for tenant:', activeTenantId, 'Date range:', dateRange);
    setLoading(true);
    
    try {
      // Load reports and SMS data in parallel
      const [avgWaitResponse, throughputResponse, hourlyActivityResponse, smsResponse] = await Promise.all([
        apiClient.getAvgWaitTimesReport({
          tenant: activeTenantId,
          date_from: dateRange.start,
          date_to: dateRange.end,
          timezone: userTimezone
        }),
        apiClient.getQueueThroughputReport({
          tenant: activeTenantId,
          date_from: dateRange.start,
          date_to: dateRange.end,
          timezone: userTimezone
        }),
        apiClient.getHourlyActivityReport({
          tenant: activeTenantId,
          date_from: dateRange.start,
          date_to: dateRange.end,
          timezone: userTimezone
        }),
        apiClient.getSmsAnalytics(activeTenantId, {
          date_from: dateRange.start,
          date_to: dateRange.end,
          timezone: userTimezone
        })
      ]);

      // Calculate total SMS including messages not tied to queues (filtered by date range and timezone)
      const totalSmsCount = (smsResponse?.total_inbound || 0) + (smsResponse?.total_outbound || 0);
      console.log('SMS Analytics Response:', {
        total_inbound: smsResponse?.total_inbound,
        total_outbound: smsResponse?.total_outbound,
        totalSmsCount,
        dateRange,
        timezone: userTimezone
      });

      // Transform API data to component format
      const queueDetails = (avgWaitResponse?.data || []).map((item: any) => {
        const avgWaitMinutes = typeof item.avg_wait_minutes === 'string' ? parseFloat(item.avg_wait_minutes) : (item.avg_wait_minutes || 0);
        return {
          queue: `${item.location} - ${item.service}`,
          customers: item.sample_size || 0,
          journeys: item.sample_size || 0,  // Journey count = root records
          unique_customers: item.unique_customers || 0,  // Unique phone numbers
          avgWait: isNaN(avgWaitMinutes) ? 0 : avgWaitMinutes
        };
      });

      const avgWaitTimes = queueDetails;

      // Transform throughput data
      const throughput = (throughputResponse?.data || []).map((item: any) => ({
        date: item.time_period,
        completed: item.total_processed || 0
      }));

      // Transform wait times by day using weighted average from avgWaitResponse
      const waitTimesByDay = (avgWaitResponse?.data || []).reduce((acc: any[], item: any) => {
        const avgWaitMinutes = typeof item.avg_wait_minutes === 'string' ? parseFloat(item.avg_wait_minutes) : (item.avg_wait_minutes || 0);
        const sampleSize = item.sample_size || 0;
        const date = item.date || item.time_period;
        
        const existingDay = acc.find(day => day.date === date);
        if (existingDay) {
          existingDay.totalWeight += avgWaitMinutes * sampleSize;
          existingDay.totalSamples += sampleSize;
          existingDay.avgWait = existingDay.totalSamples > 0 ? Math.round((existingDay.totalWeight / existingDay.totalSamples) * 10) / 10 : 0;
        } else {
          acc.push({
            date,
            avgWait: isNaN(avgWaitMinutes) ? 0 : Math.round(avgWaitMinutes * 10) / 10,
            totalWeight: avgWaitMinutes * sampleSize,
            totalSamples: sampleSize
          });
        }
        return acc;
      }, []).map(({ date, avgWait }: any) => ({ date, avgWait }));
      
      // Use real hourly wait time data from hourly activity API
      console.log('=== HOURLY ACTIVITY DEBUG ===');
      console.log('Raw hourly activity response:', hourlyActivityResponse?.data);
      
      const waitTimesByHour = (hourlyActivityResponse?.data || [])
        .filter((item: any) => item.avg_wait_minutes > 0) // Only show hours with actual wait times
        .map((item: any) => ({
          hour: item.hour,
          avgWait: item.avg_wait_minutes || 0
        }))
        .sort((a, b) => a.hour.localeCompare(b.hour));
      
      console.log('Filtered waitTimesByHour:', waitTimesByHour);
      console.log('Hours with wait times:', waitTimesByHour.length);

      console.log('=== REPORTS DEBUG ===');
      console.log('Queue Performance Card will show:');
      console.log('- Total Customers:', queueDetails.reduce((sum, item) => sum + item.customers, 0));
      console.log('- Total SMS (from SMS Analytics):', totalSmsCount);
      console.log('- Active Queues:', queueDetails.length);
      console.log('- Avg Wait Time calculation will use:', queueDetails.map(q => ({ queue: q.queue, avgWait: q.avgWait, type: typeof q.avgWait })));
      console.log('- Wait Times by Hour data points:', waitTimesByHour.length);
      console.log('- Wait Times by Hour sample:', waitTimesByHour.slice(0, 5));

      // Generate customers by day data
      const customersByDay = throughput.map((item: any) => ({
        date: item.date,
        customers: item.completed
      }));

      // Generate customers by queue data (now using queueDetails directly)
      const customersByQueue = queueDetails;

      // Use real hourly activity data
      const hourlyActivity = (hourlyActivityResponse?.data || []).map((item: any) => ({
        hour: item.hour,
        entries: item.entries || 0,
        service_starts: item.service_starts || 0,
        completions: item.completions || 0
      }));
      
      // Generate throughput by hour from hourly activity data
      const throughputByHour = hourlyActivity.map((item: any) => ({
        hour: item.hour,
        customers: item.completions // Use completions as throughput
      }));

      // Generate overview table data with real SMS data
      const smsLocationBreakdown = (smsResponse?.location_breakdown || []);
      console.log('SMS Location Breakdown:', smsLocationBreakdown);
      console.log('Queue Details for matching:', queueDetails.map(q => q.queue));
      
      const overviewTable = queueDetails.map((item: any) => {
        // Find matching SMS data for this queue - try multiple matching strategies
        let queueSms = smsLocationBreakdown.find((sms: any) => 
          `${sms.location_id} / ${sms.service_id}` === item.queue
        );
        
        // If no exact match, try without spaces
        if (!queueSms) {
          queueSms = smsLocationBreakdown.find((sms: any) => 
            `${sms.location_id}/${sms.service_id}` === item.queue.replace(' - ', '/')
          );
        }
        
        // If still no match, try partial matching
        if (!queueSms) {
          queueSms = smsLocationBreakdown.find((sms: any) => 
            item.queue.includes(sms.location_id) && item.queue.includes(sms.service_id)
          );
        }
        
        console.log(`Queue: ${item.queue}, Found SMS:`, queueSms);
        
        return {
          queue: item.queue,
          avgWait: item.avgWait,
          throughput: item.customers,
          customers: item.customers,
          smsIn: queueSms?.inbound || 0,
          smsOut: queueSms?.outbound || 0,
          smsTotal: (queueSms?.inbound || 0) + (queueSms?.outbound || 0),
          serviceTime: Math.round((item.avgWait * 1.2) * 10) / 10 // Estimate service time as 120% of wait time with 1 decimal
        };
      });

      // Debug SMS totals comparison
      const overviewTableSmsTotal = overviewTable.reduce((sum, item) => sum + item.smsTotal, 0);
      console.log('=== SMS TOTALS COMPARISON ===');
      console.log('SMS Analytics API Total:', totalSmsCount);
      console.log('Overview Table SMS Total:', overviewTableSmsTotal);
      console.log('Difference:', totalSmsCount - overviewTableSmsTotal);
      console.log('Overview Table Details:', overviewTable.map(item => ({ queue: item.queue, smsTotal: item.smsTotal, smsIn: item.smsIn, smsOut: item.smsOut })));

      console.log('=== FINAL REPORT DATA ===');
      console.log('waitTimesByHour final:', waitTimesByHour);
      
      setReportData({
        avgWaitTimes,
        queueDetails,
        throughput,
        waitTimesByDay,
        waitTimesByHour,
        customersByDay,
        customersByQueue,
        throughputByHour,
        hourlyActivity,
        overviewTable,
        totalSmsCount
      });
    } catch (error) {
      console.error('Failed to load report data:', error);
      // Show placeholder data when APIs fail
      setReportData({
        avgWaitTimes: [
          { queue: 'API Error - Check Console', customers: 0, avgWait: 0 }
        ],
        queueDetails: [
          { queue: 'API Error - Check Console', customers: 0, avgWait: 0 }
        ],
        throughput: [
          { date: 'API Error', completed: 0 }
        ],
        waitTimesByDay: [],
        waitTimesByHour: [],
        customersByDay: [],
        customersByQueue: [],
        throughputByHour: [],
        hourlyActivity: [],
        overviewTable: []
      });
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = (data: any[], filename: string) => {
    console.log('Exporting CSV:', filename, 'Data length:', data.length, 'Data:', data);
    
    if (!data || data.length === 0) {
      console.warn('No data to export for', filename);
      alert('No data available to export');
      return;
    }
    
    try {
      const headers = Object.keys(data[0]);
      const csvContent = [
        headers.join(','),
        ...data.map(row => headers.map(header => `"${row[header] || ''}"`).join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}_${dateRange.start}_to_${dateRange.end}.csv`;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      console.log('CSV export completed for', filename);
    } catch (error) {
      console.error('CSV export failed:', error);
      alert('Failed to export CSV: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  const exportReport = (reportType: string) => {
    console.log('Export report requested:', reportType, 'Report data:', reportData);
    
    switch (reportType) {
      case 'wait-times':
        exportToCSV(reportData.avgWaitTimes, 'wait_times_report');
        break;
      case 'throughput':
        exportToCSV(reportData.throughput, 'throughput_report');
        break;
      case 'customer-details':
        exportToCSV(reportData.queueDetails, 'customer_details_report');
        break;
      case 'overview':
        exportToCSV(reportData.overviewTable, 'overview_report');
        break;
      default:
        console.warn('Unknown report type:', reportType);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Reports & Analytics</h1>
        <p className="text-muted-foreground">
          Analyze queue performance and customer behavior patterns {isSuperAdmin && <Badge variant="outline" className="ml-2">Super Admin</Badge>}
        </p>
      </div>

      {/* Date Range Filter */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Report Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {datePresets.map((preset) => (
                <Button
                  key={preset.id}
                  variant={selectedPreset === preset.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setSelectedPreset(preset.id);
                    applyDatePreset(preset.id);
                  }}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
            
            {selectedPreset === 'custom' && (
              <div className="flex items-end gap-4">
                <div className="grid w-full max-w-sm items-center gap-1.5">
                  <Label htmlFor="start-date">Start Date</Label>
                  <Input
                    type="date"
                    id="start-date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  />
                </div>
                <div className="grid w-full max-w-sm items-center gap-1.5">
                  <Label htmlFor="end-date">End Date</Label>
                  <Input
                    type="date"
                    id="end-date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  />
                </div>
              </div>
            )}
            
            <div className="flex items-center gap-2">
              <Button onClick={loadReportData} disabled={loading} className={getPrimaryButtonClass()}>
                {loading ? 'Loading...' : 'Update Reports'}
              </Button>
              <span className="text-sm text-muted-foreground">
                {dateRange.start} to {dateRange.end}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="wait-times" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Wait Times
          </TabsTrigger>
          <TabsTrigger value="throughput" className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Throughput
          </TabsTrigger>
          <TabsTrigger value="customers" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Customers
          </TabsTrigger>
          <TabsTrigger value="sms" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            SMS Analytics
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Queue Performance</CardTitle>
                <CardDescription>Average metrics for selected period</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm">Avg Wait Time</span>
                  <Badge variant="outline">
                    {(() => {
                      if (reportData.queueDetails.length === 0) return '0m 0s';
                      const avgMinutes = reportData.queueDetails.reduce((sum, item) => {
                        const waitTime = typeof item.avgWait === 'string' ? parseFloat(item.avgWait) : item.avgWait;
                        return sum + (isNaN(waitTime) ? 0 : waitTime);
                      }, 0) / reportData.queueDetails.length;
                      
                      if (isNaN(avgMinutes) || avgMinutes === 0) return '0m 0s';
                      
                      const minutes = Math.floor(avgMinutes);
                      const seconds = Math.round((avgMinutes - minutes) * 60);
                      if (seconds >= 60) {
                        return `${minutes + 1}m 0s`;
                      }
                      return `${minutes}m ${seconds}s`;
                    })()}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">Total Customers</span>
                  <Badge variant="outline">
                    {reportData.queueDetails.reduce((sum, item) => sum + item.customers, 0)}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">Total SMS</span>
                  <Badge variant="outline">
                    {reportData.totalSmsCount || 0}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">Active Queues</span>
                  <Badge variant="outline">
                    {reportData.queueDetails.length}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Top Performing Queues</CardTitle>
                <CardDescription>Highest throughput queues</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {reportData.queueDetails
                  .sort((a, b) => b.customers - a.customers)
                  .slice(0, 3)
                  .map((queue, index) => (
                    <div key={index} className="flex justify-between items-center p-2 border rounded">
                      <div>
                        <div className="font-medium text-sm">{queue.queue}</div>
                        <div className="text-xs text-muted-foreground">{queue.customers} customers</div>
                      </div>
                      <Badge variant="secondary">{queue.avgWait}m avg</Badge>
                    </div>
                  ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Export Reports</CardTitle>
                <CardDescription>Download detailed analytics</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => exportReport('overview')}
                >
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Overview Report
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => exportReport('wait-times')}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Wait Times Report
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => exportReport('throughput')}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Throughput Report
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => exportReport('customer-details')}
                >
                  <Users className="h-4 w-4 mr-2" />
                  Customer Details
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Overview Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Queue Performance Overview
                <div className="flex items-center gap-2">
                  <Label htmlFor="daily-breakdown" className="text-sm">Daily Breakdown</Label>
                  <Switch 
                    id="daily-breakdown"
                    checked={showDailyBreakdown}
                    onCheckedChange={setShowDailyBreakdown}
                  />
                </div>
              </CardTitle>
              <CardDescription>
                {showDailyBreakdown ? 'Daily performance metrics by queue' : 'Total performance metrics by queue'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Queue</th>
                      <th className="text-right p-2">Avg Wait</th>
                      <th className="text-right p-2">Service Time</th>
                      <th className="text-right p-2">Throughput</th>
                      <th className="text-right p-2">Customers</th>
                      <th className="text-right p-2">SMS In</th>
                      <th className="text-right p-2">SMS Out</th>
                      <th className="text-right p-2">SMS Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.overviewTable.map((row, index) => (
                      <tr key={index} className="border-b hover:bg-gray-50">
                        <td className="p-2 font-medium">{row.queue}</td>
                        <td className="p-2 text-right">{row.avgWait}m</td>
                        <td className="p-2 text-right">{row.serviceTime.toFixed(1)}m</td>
                        <td className="p-2 text-right">{row.throughput}</td>
                        <td className="p-2 text-right">{row.customers}</td>
                        <td className="p-2 text-right">{row.smsIn}</td>
                        <td className="p-2 text-right">{row.smsOut}</td>
                        <td className="p-2 text-right font-medium">{row.smsTotal}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="wait-times" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Weighted Average Wait Times by Day</CardTitle>
                <CardDescription>Sample-size weighted average wait times by day</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={reportData.waitTimesByDay}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis label={{ value: 'Minutes', angle: -90, position: 'insideLeft' }} />
                      <Tooltip formatter={(value) => [`${value} min`, 'Avg Wait']} />
                      <Bar dataKey="avgWait" fill="#2563eb" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Wait Times by Hour</CardTitle>
                <CardDescription>Average wait times by hour customers joined queues</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={reportData.waitTimesByHour}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="hour" />
                      <YAxis label={{ value: 'Minutes', angle: -90, position: 'insideLeft' }} />
                      <Tooltip formatter={(value) => [`${value} min`, 'Avg Wait']} />
                      <Bar dataKey="avgWait" fill="#16a34a" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Queue Performance Summary</CardTitle>
              <CardDescription>Wait time performance by queue and day</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Queue</th>
                      <th className="text-right p-2">Avg Wait Time</th>
                      <th className="text-right p-2">Customers</th>
                      <th className="text-right p-2">Performance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.queueDetails.map((item, index) => (
                      <tr key={index} className="border-b hover:bg-gray-50">
                        <td className="p-2 font-medium">{item.queue}</td>
                        <td className="p-2 text-right">{item.avgWait}m</td>
                        <td className="p-2 text-right">{item.customers}</td>
                        <td className="p-2 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-16 bg-gray-200 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full ${
                                  item.avgWait <= 10 ? 'bg-green-600' : 
                                  item.avgWait <= 20 ? 'bg-yellow-600' : 'bg-red-600'
                                }`}
                                style={{ width: `${Math.min(item.avgWait / 30 * 100, 100)}%` }}
                              ></div>
                            </div>
                            <Badge variant={item.avgWait <= 10 ? 'default' : item.avgWait <= 20 ? 'secondary' : 'destructive'}>
                              {item.avgWait <= 10 ? 'Good' : item.avgWait <= 20 ? 'Fair' : 'Poor'}
                            </Badge>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="throughput" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Customers by Day</CardTitle>
                <CardDescription>Daily customer volume with totals</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={reportData.customersByDay}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis label={{ value: 'Customers', angle: -90, position: 'insideLeft' }} />
                      <Tooltip formatter={(value) => [`${value}`, 'Customers']} />
                      <Bar dataKey="customers" fill="#16a34a" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 p-3 bg-gray-50 rounded">
                  <div className="text-sm font-medium">Total Customers: {reportData.customersByDay.reduce((sum, item) => sum + item.customers, 0)}</div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Queue Entries by Hour</CardTitle>
                <CardDescription>When customers joined queues</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={reportData.hourlyActivity}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="hour" />
                      <YAxis label={{ value: 'Entries', angle: -90, position: 'insideLeft' }} />
                      <Tooltip formatter={(value) => [`${value}`, 'Queue Entries']} />
                      <Bar dataKey="entries" fill="#3b82f6" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Service Starts by Hour</CardTitle>
                <CardDescription>When customers began being served</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={reportData.hourlyActivity}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="hour" />
                      <YAxis label={{ value: 'Service Starts', angle: -90, position: 'insideLeft' }} />
                      <Tooltip formatter={(value) => [`${value}`, 'Service Starts']} />
                      <Bar dataKey="service_starts" fill="#f59e0b" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Completions by Hour</CardTitle>
                <CardDescription>When customers completed service</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={reportData.hourlyActivity}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="hour" />
                      <YAxis label={{ value: 'Completions', angle: -90, position: 'insideLeft' }} />
                      <Tooltip formatter={(value) => [`${value}`, 'Completions']} />
                      <Bar dataKey="completions" fill="#10b981" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Hourly Activity Summary</CardTitle>
              <CardDescription>Peak hours and activity patterns</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="p-3 bg-blue-50 rounded">
                  <div className="text-sm text-blue-600 font-medium">Peak Entry Hour</div>
                  <div className="text-lg font-bold">
                    {(() => {
                      const peak = reportData.hourlyActivity.reduce((max, item) => 
                        item.entries > max.entries ? item : max, 
                        { hour: 'N/A', entries: 0 }
                      );
                      return `${peak.hour} (${peak.entries} entries)`;
                    })()}
                  </div>
                </div>
                <div className="p-3 bg-yellow-50 rounded">
                  <div className="text-sm text-yellow-600 font-medium">Peak Service Hour</div>
                  <div className="text-lg font-bold">
                    {(() => {
                      const peak = reportData.hourlyActivity.reduce((max, item) => 
                        item.service_starts > max.service_starts ? item : max, 
                        { hour: 'N/A', service_starts: 0 }
                      );
                      return `${peak.hour} (${peak.service_starts} starts)`;
                    })()}
                  </div>
                </div>
                <div className="p-3 bg-green-50 rounded">
                  <div className="text-sm text-green-600 font-medium">Peak Completion Hour</div>
                  <div className="text-lg font-bold">
                    {(() => {
                      const peak = reportData.hourlyActivity.reduce((max, item) => 
                        item.completions > max.completions ? item : max, 
                        { hour: 'N/A', completions: 0 }
                      );
                      return `${peak.hour} (${peak.completions} completions)`;
                    })()}
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="text-sm font-medium mb-2">Daily Throughput Summary</div>
                {reportData.throughput.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border rounded">
                    <span className="text-sm font-medium">{item.date}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-32 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-600 h-2 rounded-full" 
                          style={{ width: `${Math.min(item.completed / 150 * 100, 100)}%` }}
                        ></div>
                      </div>
                      <Badge variant="outline">{item.completed} customers</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="customers" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Total Customers</CardTitle>
                <CardDescription>Customer volume summary</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold mb-2">
                  {reportData.queueDetails.reduce((sum, item) => sum + item.customers, 0)}
                </div>
                <p className="text-sm text-muted-foreground mb-4">Total customers served</p>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Unique Customers:</span>
                    <span className="font-medium">{reportData.queueDetails.reduce((sum, item) => sum + (item.unique_customers || item.customers), 0)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Active Queues:</span>
                    <span className="font-medium">{reportData.queueDetails.length}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Avg per Queue:</span>
                    <span className="font-medium">
                      {reportData.queueDetails.length > 0 
                        ? Math.round(reportData.queueDetails.reduce((sum, item) => sum + item.customers, 0) / reportData.queueDetails.length)
                        : 0}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Customers by Queue</CardTitle>
                <CardDescription>Total customers served per queue</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {reportData.queueDetails
                    .sort((a, b) => b.customers - a.customers)
                    .map((queue, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{queue.queue}</div>
                      </div>
                      <div className="flex items-center gap-3 ml-4">
                        <div className="w-24 bg-gray-200 rounded-full h-3">
                          <div 
                            className="bg-blue-500 h-3 rounded-full" 
                            style={{ width: `${Math.min((queue.customers / Math.max(...reportData.queueDetails.map(q => q.customers))) * 100, 100)}%` }}
                          ></div>
                        </div>
                        <div className="text-sm font-medium w-8 text-right">{queue.customers}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Journeys by Queue</CardTitle>
                <CardDescription>Total customer journeys per queue</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {reportData.queueDetails
                    .sort((a, b) => (b.journeys || 0) - (a.journeys || 0))
                    .map((queue, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{queue.queue}</div>
                      </div>
                      <div className="flex items-center gap-3 ml-4">
                        <div className="w-24 bg-gray-200 rounded-full h-3">
                          <div 
                            className="bg-green-500 h-3 rounded-full" 
                            style={{ width: `${Math.min(((queue.journeys || 0) / Math.max(...reportData.queueDetails.map(q => q.journeys || 0))) * 100, 100)}%` }}
                          ></div>
                        </div>
                        <div className="text-sm font-medium w-8 text-right">{queue.journeys || 0}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Unique Customers by Queue</CardTitle>
                <CardDescription>Distinct customers served per queue</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {reportData.queueDetails
                    .sort((a, b) => (b.unique_customers || 0) - (a.unique_customers || 0))
                    .map((queue, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{queue.queue}</div>
                      </div>
                      <div className="flex items-center gap-3 ml-4">
                        <div className="w-24 bg-gray-200 rounded-full h-3">
                          <div 
                            className="bg-orange-500 h-3 rounded-full" 
                            style={{ width: `${Math.min(((queue.unique_customers || 0) / Math.max(...reportData.queueDetails.map(q => q.unique_customers || 0))) * 100, 100)}%` }}
                          ></div>
                        </div>
                        <div className="text-sm font-medium w-8 text-right">{queue.unique_customers || 0}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Customers by Day</CardTitle>
              <CardDescription>Daily customer volume</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={reportData.customersByDay}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis label={{ value: 'Customers', angle: -90, position: 'insideLeft' }} />
                    <Tooltip formatter={(value) => [`${value}`, 'Customers']} />
                    <Bar dataKey="customers" fill="#16a34a" />
                  </BarChart>
                </ResponsiveContainer>
            </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Queue Details</CardTitle>
              <CardDescription>Customer volume and performance by queue</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {reportData.queueDetails.map((queue, index) => (
                  <div key={index} className="p-4 border rounded-lg">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-medium">{queue.queue}</h4>
                        <p className="text-sm text-muted-foreground">{queue.customers} total customers</p>
                      </div>
                      <Badge variant="secondary">{queue.avgWait}m avg wait</Badge>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ width: `${Math.min(queue.customers / 100 * 100, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sms" className="space-y-4">
          {activeTenantId && (
            <SMSAnalytics 
              key={smsAnalyticsKey}
              tenantId={activeTenantId} 
              dateRange={dateRange}
              userTimezone={userTimezone}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}